import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import 'antd/dist/reset.css'
import 'App.scss'
import 'styles/legacyPageStyles'
import { AccountProvider } from 'components/Provider/account';
import { mixGetV1 } from 'services/mixin';
import { API_URL } from 'env';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { NotificationProvider } from 'hooks/useNotification'
// FCM (commented out below) needs: firebase/app, firebase/messaging, services/firebase_config,
// services/fmc_token_upload.

// Every page is its own chunk, so a visit downloads only the page it opens instead of the
// whole app (charts, PDF tools, maps, wallet SDKs...). Matters most on phones.
const Profiles = lazy(() => import('pages/Profiles'))
const AccountsOverview = lazy(() => import('pages/Dashboard/AccountsOverview'))
const FreeTrial = lazy(() => import('pages/Dashboard/AccountsOverview/FreeTrial'))
const MaTrixRiskReward = lazy(() => import('pages/Dashboard/AccountsOverview/MaTrixRiskReward'))
const Numerologist = lazy(() => import('pages/Dashboard/Numerologist'))
const NumberlogistProduct = lazy(() => import('pages/Dashboard/Numerologist/Products'))
const ProductDetail = lazy(() => import('pages/Dashboard/Numerologist/Products/ProductDetail'))
const SignUp = lazy(() => import('pages/SignUp'))
const ForgetPassword = lazy(() => import('pages/SignUp/ForgetPassword'))
const AccountManager = lazy(() => import('pages/Dashboard/Manager'))
const AccountInfoPage = lazy(() => import('pages/Profiles/accountInfoPage'))
const AccountSecurityoPage = lazy(() => import('pages/Profiles/accountSecurity'))
const EquitySimulator = lazy(() => import('pages/Dashboard/EquitySimulator'))
const Numerology = lazy(() => import('pages/Numerology'))
const DetailStar = lazy(() => import('pages/Numerology/DetailStar'))
const ProfileSetup = lazy(() => import('pages/SignUp/Step/ProfileSetup'))
const BehavioralPerformance = lazy(() => import('pages/Dashboard/BehavioralPerformance'))
const TelegramCallback = lazy(() => import('pages/TelegramCallback'))
const CallbackLogin = lazy(() => import('pages/Login/CallbackLogin'))
const NotificationMainPage = lazy(() => import('pages/Notifications'))
const NotificationDetailPage = lazy(() => import('pages/Notifications/detail'))
const Affiliate = lazy(() => import('pages/Affiliate'))
const TeamsDashboard = lazy(() => import('pages/Profiles/Team/index'))
const TeamDetails = lazy(() => import('pages/Profiles/Team/teamDetail'))
const OTPPage = lazy(() => import('pages/SignUp/Otp'))
const Academy = lazy(() => import('pages/Academy'))
const QuizPage = lazy(() => import('pages/Academy/Quiz'))
const PickAccount = lazy(() => import('pages/Academy/PickAccount'))
const MainStorePage = lazy(() => import('pages/Store'))
const StoreProductDetail = lazy(() => import('pages/Store/detail'))
const StoreUserInventory = lazy(() => import('pages/Store/inventory'))
const InventoryDetail = lazy(() => import('pages/Store/component/inventoryDetail'))
const ToolFrame = lazy(() => import('pages/Store/ToolFrame'))
const FreePDF = lazy(() => import('pages/pdf/pdfFree'))
const TeamMemberTBI = lazy(() => import('pages/Profiles/Team/TeamMember/TBI'))
const DownloadPage = lazy(() => import('pages/Bots/Download'))
const HelpCenter = lazy(() => import('pages/Help'))
const HelpDetail = lazy(() => import('pages/Help/detail'))
const LibraryPage = lazy(() => import('pages/Library'))
const LibraryPdfViewer = lazy(() => import('pages/Library/pdf_viewer'))
const MetrixPDF = lazy(() => import('pages/Dashboard/Metrix/pdf'))
const DashBoardMetrix = lazy(() => import('pages/Dashboard/Metrix/Dashboard'))
const Leaderboard = lazy(() => import('pages/Academy/Leaderboard'))
const CommunityLeaderboard = lazy(() => import('pages/Community/LeaderBoard'))
const CommunityExpert = lazy(() => import('pages/Community/Expert'))
const CommunityManagerNetwork = lazy(() => import('pages/Community/Manager_Network'))
const CapitalManagement = lazy(() => import('pages/CapitalManagement'))
const MetrixDashboardV2 = lazy(() => import('pages/Dashboard/Metrix_v2/Live'))
// Wallet SDKs are only needed once signed in (header wallet button, account page).
const Web3Provider = lazy(() => import('./wagmi_config/Web3Provider'))

const RouteFallback = () => (
  <div className="route-fallback" role="status" aria-label="Loading">
    <span className="route-fallback__spinner" />
  </div>
)

// Initialize Firebase
declare global {
  interface Window {
    DOMAIN_API: string;
    SITE_ID: string;
  }
}

const queryClient = new QueryClient()
const App: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const token = localStorage.getItem('token')
  const [checkLogin, setCheckLogin] = useState(false)
  const params = new URLSearchParams(new URL(window.location.href).search);
  const code1 = params.get("code");

  // const app = initializeApp(firebaseConfig);
  // const [messaging, setMessaging] = useState<Messaging | null>(null);

  // useEffect(() => {
  //   isSupported().then((supported) => {
  //     if (supported) {
  //       const msg = getMessaging(app);
  //       setMessaging(msg);
  //       console.log("FCM được hỗ trợ trên trình duyệt này.");
  //     } else {
  //       console.log("Trình duyệt không hỗ trợ FCM (Ví dụ: Safari iOS chưa Add to Home Screen).");
  //     }
  //   }).catch(err => console.error("Lỗi kiểm tra support:", err));
  // }, [app]);

  // 1. Correct way to register the Service Worker and get the FCM Device Token
  // async function requestNotificationPermission() {
  //   if (!messaging) return;
  //   try {
  //     // Request permission from the user
  //     const permission = await Notification.requestPermission();

  //     if (permission === "granted") {
  //       // Register the service worker manually to ensure it points to your public folder
  //       const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  //       await navigator.serviceWorker.ready; // Wait until it's fully active

  //       // Get the ACTUAL FCM token required for sending messages
  //       const fcmToken = await getToken(messaging, {
  //         vapidKey: vapidKey,
  //         serviceWorkerRegistration: registration 
  //       });

  //       // COPY THIS TOKEN FROM YOUR CONSOLE LOG!
  //       console.log('Your FCM Device Token:', fcmToken);
  //       if (fcmToken && token) {
  //         uploadFmcToken(fcmToken);
  //       }

  //     } else {
  //       console.log('Notification permission denied.');
  //     }
  //   } catch (err) {
  //     console.error('An error occurred while registering:', err);
  //   }
  // }

  // useEffect(() => {
  //   if (token && messaging) {
  //     requestNotificationPermission();
  //   }
  // }, [token, messaging]);

  // // 2. Correct way to listen for foreground messages (when app is open)
  // useEffect(() => {
  //   if (messaging) {
  //     const unsubscribe = onMessage(messaging, (payload) => {
  //       console.log('Message received in foreground: ', payload);
  //       alert(`Notification: ${payload.notification?.title}\n${payload.notification?.body}`);
  //     });
  //     return () => unsubscribe();
  //   }
  // }, [messaging]);

  function getRedirectUrl(url: string): string | null {
    // Matches 'redirect_url=' followed by everything up to the next '&' or the end of the string
    const match = url.match(/[?&]redirect_url=([^&#]*)/);

    if (!match) {
      return null;
    }

    // decodeURIComponent handles any URL encoding (e.g., %2F instead of /)
    return decodeURIComponent(match[1]);
  }

  function getQRInviteData(url: string): string | null {
    // Matches 'join-via-qr' followed by an optional query string (starting with ?)
    const match = url.match(/join-via-qr(.*)/);
    if (!match) {
      return null;
    }
    // Return the query string if present, otherwise return 'true' to indicate the path was matched
    return match[1] ? decodeURIComponent(match[1]) : "true";
  }

  useEffect(() => {
    const url = window.location.href;
    var redirect_url = getRedirectUrl(url)
    const inviteQR = getQRInviteData(url)
    if (inviteQR) {
      localStorage.setItem('join_via_qr', inviteQR);
      localStorage.setItem('redirect_url', '/teams');
    }
    if (redirect_url && redirect_url.length > 0) {
      if (!token) {
        localStorage.setItem('redirect_url', redirect_url);
      }
      window.location.href = redirect_url
    }
  }, [])

  const handleCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    // Verify state
    // const savedState = sessionStorage.getItem('google_oauth_state');

    if (code) {
      try {
        const response = await fetch(`${API_URL}/api/v1/google/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state })
        });

        const data = await response.json();
        if (data.status) {
          localStorage.setItem('token', data.data.access_token);
          // localStorage.setItem('user', JSON.stringify(data.data.info))
          // window.location.href = '/';
          const res2 = await mixGetV1(`/api/v1/be/user/${data.data.info.id}`)

          if (res2.status) {
            localStorage.setItem('user', JSON.stringify(res2.data))

            // message.success('Login successful!');
            window.location.reload()
            return
          }
        }
      } catch (error) {
        console.error('Login error:', error);
      }
    }
    // Failed or expired Google sign-in: back to the login form instead of a blank screen.
    navigate('/sign-up', { replace: true })
  };

  useEffect(() => {
    const notLogin = ['/login', '/sign-up', '/forget-password', '/reset-password', '/verify-otp']
    if (notLogin.includes(location.pathname) === false && !token && code1) {
      handleCallback()
      return
    }
    if (notLogin.includes(location.pathname) === false && !token && !code1) {
      navigate('/sign-up')
    }
    if (notLogin.includes(location.pathname) === true && token) {
      navigate('/')
    }
    if (location.pathname === '/teams/join-via-qr' && token) {
      navigate('/teams')
    }

    setCheckLogin(true)

  }, [navigate, location.pathname, token, code1])


  return (
    <>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <AccountProvider>
            <Suspense fallback={<RouteFallback />}>
              {!checkLogin ? <RouteFallback /> :
                <Web3Gate enabled={!!token}>
                <Routes>
                  <Route path='/' element={<Navigate to="/accounts-overview" replace />} />
                  <Route path='/login' element={<SignUp />} />
                  {/* <Route path=':tradePlatform/test/:id' element={<MetrixDashboardV2 />} /> */}
                  <Route path='/sign-up' element={<SignUp />} />
                  <Route path='/sign-up/step' element={<ProfileSetup />} />
                  <Route path='/forget-password' element={<ForgetPassword />} />
                  {/* Password reset is the OTP flow on /forget-password; old links land there. */}
                  <Route path='/reset-password' element={<Navigate to="/forget-password" replace />} />
                  <Route path='/verify-otp' element={<OTPPage />} />
                  <Route path='/academy' element={<Academy />} />
                  <Route path='/academy/quiz' element={<QuizPage />} />
                  <Route path='/academy/quiz/dashboard' element={<QuizPage />} />
                  <Route path='/academy/quiz/foundation' element={<QuizPage />} />
                  <Route path='/academy/quiz/day/:day' element={<QuizPage />} />
                  <Route path='/academy/quiz/:day' element={<QuizPage />} />
                  <Route path='/academy/leaderboard' element={<Leaderboard />} />
                  <Route path='/community/leaderboard' element={<CommunityLeaderboard />} />
                  <Route path='/community/expert' element={<CommunityExpert />} />
                  <Route path='/community/manager-network' element={<CommunityManagerNetwork />} />
                  <Route path='/capital-management' element={<CapitalManagement />} />
                  <Route path='/capital-management/managers/:cmId' element={<CapitalManagement />} />
                  <Route path='/capital-management/:section' element={<CapitalManagement />} />
                  <Route path='/community' element={<CommunityLeaderboard />} />
                  <Route path='/academy/my-account' element={<PickAccount />} />
                  <Route path='/profile' element={<Profiles />} />
                  <Route path=':tradePlatform/account-live-metrix/:id' element={<MetrixDashboardV2 />} />
                  <Route path=':tradePlatform/account-metrix/:id' element={<DashBoardMetrix />} />
                  <Route path='/accounts-overview' element={<AccountsOverview />} />
                  <Route path='/accounts-overview/matrix-risk-reward' element={<MaTrixRiskReward />} />
                  <Route path='/free-trial' element={<FreeTrial />} />
                  <Route path='/manager' element={<AccountManager />} />
                  <Route path='/profile/account' element={<AccountInfoPage />} />
                  <Route path='/profile/security' element={<AccountSecurityoPage />} />
                  <Route path='/teams' element={<TeamsDashboard />} />
                  <Route path='/teams/:id' element={<TeamDetails />} />
                  <Route path='/teams/:team/member/:id' element={<TeamMemberTBI />} />
                  <Route path='/profile/inventory' element={<StoreUserInventory />} />
                  <Route path='/profile/inventory/:id' element={<InventoryDetail />} />
                  <Route path='/equity-simulator' element={<EquitySimulator />} />
                  <Route path="/TBI" element={<Numerology />} />
                  <Route path="/TBI/:slug/:id" element={<DetailStar />} />
                  <Route path="/numerologist" element={<Numerologist />} />
                  <Route path="/numerologist-products" element={<NumberlogistProduct />} />
                  <Route path="/numerologist-products/:id" element={<ProductDetail />} />
                  <Route path=":tradePlatform/behavioral-performance/:id" element={<BehavioralPerformance />} />
                  <Route path="/api/v1/telegram/callback" element={<TelegramCallback />} />
                  <Route path='/callback-login' element={<CallbackLogin />} />
                  <Route path='/notifications' element={<NotificationMainPage />} />
                  <Route path='/notifications/:id' element={<NotificationDetailPage />} />
                  <Route path='/profile/affiliate' element={<Affiliate />} />
                  <Route path='/stores' element={<MainStorePage />} />
                  <Route path='/stores/:id' element={<StoreProductDetail />} />
                  <Route path="/stores/view/:id" element={<ToolFrame />} />
                  <Route path='/TBI_pdf' element={<FreePDF />} />
                  <Route path='/platform/download' element={<DownloadPage />} />
                  <Route path='/help' element={<HelpCenter />} />
                  <Route path='/help/:slug' element={<HelpDetail />} />
                  <Route path='/library' element={<LibraryPage />} />
                  <Route path='/library/view/:id' element={<LibraryPdfViewer />} />
                  <Route path='/library/:folderId' element={<LibraryPage />} />
                  {/* <Route path='test/:tradePlatform/account-live-metrix/:id' element={<LiveMetrix />} />
            <Route path='test/:tradePlatform/account-metrix/:id' element={<DashBoardMetrix />} /> */}
                  <Route path=':platform/:account_number/pdf-custom' element={<MetrixPDF />} />
                </Routes >
                </Web3Gate>
              }
            </Suspense>
          </AccountProvider>
        </NotificationProvider>
      </QueryClientProvider>
    </>
  )
}

// Signed-out visitors only ever see the auth pages, which need no wallet.
const Web3Gate: React.FC<{ enabled: boolean; children: React.ReactNode }> = ({ enabled, children }) =>
  enabled ? <Web3Provider>{children}</Web3Provider> : <>{children}</>


export default App
